// Live's Transcript tab (#239, spec docs/specs/editor/captions.md): a row per caption, earliest
// first: its start, its text in a box, and on hover "Play / Seek to caption" and "Delete caption";
// the row the playhead is in is marked (and stays marked in a gap after it, as live's). Typing
// re-times the caption's words (`realign`), Enter splits the caption at the caret, Shift+Enter
// starts a new line. Footer: "Regenerate captions" (part 2), the import, and "Delete captions".
// Nothing asks first, as live's.
import { ActionIcon, Button, Textarea, Tooltip } from "@mantine/core";
import { Play, Trash2 } from "lucide-react";
import { useState, type KeyboardEvent, type ReactNode } from "react";

import { usePlayheadWatch, type Playhead } from "../playhead";
import { newId, type CaptionClip, type Timeline, type TimelineEdit } from "../timeline";
import { realign, splitAtCaret } from "./realign";
import { MAX_CAPTIONS, captionText, wordRuns } from "./words";
import classes from "./captions.module.css";

/** One of live's toasts, said in a line under the tab. */
export interface Said {
  tone: "success" | "info" | "error";
  text: string;
}

export interface TranscriptProps {
  timeline: Timeline;
  /** The playhead: the transcript renders when the caption under it changes, not at each seek. */
  playhead: Playhead;
  readOnly: boolean;
  onEdit: (edit: TimelineEdit) => void;
  onSeek: (ms: number) => void;
  onSay: (said: Said | null) => void;
  importButton: ReactNode;
  /** Live's "Regenerate captions": the tab's Generate, named for captions that exist. */
  regenerateButton: ReactNode;
}

/** Live's row time: `m:ss` of the caption's start, whole seconds, minutes unpadded. */
export function rowTime(ms: number): string {
  const seconds = Math.floor(ms / 1000);
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
}

/** Where the caret is in the caption's own text: the same word, the same place in it. */
function caretIn(typed: string, caret: number, shown: string): number {
  const typedWords = wordRuns(typed);
  const shownWords = wordRuns(shown);
  const at = typedWords.findIndex((word) => word.end > caret);
  if (at === -1) return shown.length;
  const word = typedWords[at]!;
  const target = shownWords[at];
  if (target === undefined) return shown.length;
  return target.at + Math.max(0, Math.min(caret - word.at, target.end - target.at));
}

export function Transcript(props: TranscriptProps) {
  const { timeline, playhead, readOnly, onEdit, onSay } = props;
  const captions = timeline.clips
    .filter((clip): clip is CaptionClip => clip.kind === "caption")
    .sort((one, other) => one.startMs - other.startMs);
  // Live's mark stays on the last caption the playhead was in, through a gap after it.
  const [marked, setMarked] = useState<string | null>(null);
  const playing = usePlayheadWatch(
    playhead,
    (ms) => captions.find((caption) => ms >= caption.startMs && ms < caption.endMs)?.id ?? "",
  );
  const mark = playing !== "" ? playing : captions.some((caption) => caption.id === marked) ? marked : null;
  if (mark !== marked) setMarked(mark);
  return (
    <div className={classes.transcript}>
      <ul className={classes.rows} aria-label="Transcript">
        {captions.map((caption) => (
          <Row
            key={caption.id}
            caption={caption}
            active={caption.id === mark}
            full={captions.length >= MAX_CAPTIONS}
            readOnly={readOnly}
            onEdit={onEdit}
            onSeek={props.onSeek}
            onSay={onSay}
          />
        ))}
      </ul>
      <footer className={classes.footer}>
        {props.regenerateButton}
        {props.importButton}
        <Button
          variant="subtle"
          color="gray"
          className={classes.deleteAll}
          leftSection={<Trash2 size={14} aria-hidden />}
          disabled={readOnly}
          onClick={() => {
            onEdit({ type: "removeCaptions" });
            onSay({ tone: "success", text: "Deleted all captions" });
          }}
        >
          Delete captions
        </Button>
      </footer>
    </div>
  );
}

function Row(props: {
  caption: CaptionClip;
  active: boolean;
  /** The video holds the most captions it can: no split. */
  full: boolean;
  readOnly: boolean;
  onEdit: (edit: TimelineEdit) => void;
  onSeek: (ms: number) => void;
  onSay: (said: Said | null) => void;
}) {
  const { caption, onEdit, onSay } = props;
  const lengthMs = caption.endMs - caption.startMs;
  const shown = captionText(caption.words);
  // While the box has the focus it shows what was typed (its spaces too); else the words.
  const [typed, setTyped] = useState<string | null>(null);
  const text = typed ?? shown;
  const locked = props.readOnly || caption.locked;
  const type = (value: string) => {
    setTyped(value);
    onEdit({ type: "setCaptionWords", id: caption.id, words: realign(caption.words, value, lengthMs) });
  };
  const keys = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== "Enter" || event.shiftKey || event.nativeEvent.isComposing) return;
    event.preventDefault();
    // A locked caption (or a project the member may not change) is not split.
    if (locked) return;
    if (props.full) {
      onSay({ tone: "error", text: `This video holds the most captions it can (${MAX_CAPTIONS}).` });
      return;
    }
    if (wordRuns(text).length < 2) {
      onSay({ tone: "info", text: "Cannot split a single word caption." });
      return;
    }
    const split = splitAtCaret(caption.words, caretIn(text, event.currentTarget.selectionStart, shown), lengthMs);
    if (split === null) {
      onSay({ tone: "error", text: "Failed to split caption segment" });
      return;
    }
    setTyped(null);
    onEdit({ type: "splitCaption", id: caption.id, newId: newId(), ...split });
    onSay({ tone: "success", text: "Split caption segment" });
  };
  return (
    // Live seeks on a click on the row outside its box; the play button is the keyboard's way.
    // oxlint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events
    <li
      className={classes.row}
      data-active={props.active || undefined}
      onClick={(event) => {
        if ((event.target as HTMLElement).closest("textarea, button") === null) props.onSeek(caption.startMs);
      }}
    >
      <span className={classes.rowTime}>{rowTime(caption.startMs)}</span>
      <Textarea
        className={classes.rowText}
        variant="unstyled"
        aria-label={`Caption at ${rowTime(caption.startMs)}`}
        placeholder="Caption text..."
        rows={text.includes("\n") || text.length > 28 ? 2 : 1}
        value={text}
        readOnly={locked}
        onChange={(event) => type(event.currentTarget.value)}
        onBlur={() => setTyped(null)}
        onKeyDown={keys}
      />
      <span className={classes.rowTools}>
        <Tooltip label="Play / Seek to caption">
          <ActionIcon
            variant="subtle"
            color="gray"
            size={24}
            aria-label="Play / Seek to caption"
            onClick={() => props.onSeek(caption.startMs)}
          >
            <Play size={12} />
          </ActionIcon>
        </Tooltip>
        <Tooltip label="Delete caption">
          <ActionIcon
            variant="subtle"
            color="gray"
            size={24}
            className={classes.rowDelete}
            aria-label="Delete caption"
            disabled={locked}
            onClick={() => {
              onEdit({ type: "removeClip", id: caption.id });
              onSay({ tone: "success", text: "Deleted caption" });
            }}
          >
            <Trash2 size={12} />
          </ActionIcon>
        </Tooltip>
      </span>
    </li>
  );
}
